import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  onSuccess: () => void;
}

export function InviteMemberDialog({
  open,
  onOpenChange,
  organizationId,
  onSuccess,
}: InviteMemberDialogProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { idToken } = useCognitoAuth();

  const validateEmail = (v: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const handleSubmit = useCallback(async () => {
    if (!idToken) return;
    setError(null);

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/organizations/${organizationId}/members`, { email: email.trim(), role }, { errorCode: ErrorCode.INVITE_MEMBER, token: idToken });
      setEmail('');
      setRole('member');
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      const beStatus = (err instanceof Error && 'code' in err) ? (err as BeangoError).status : 0;
      if (beStatus === 403) {
        setError('You do not have permission to send invitations. Only owners and admins can invite members.');
      } else if (beStatus === 409) {
        setError('A pending invitation already exists for this email.');
      } else {
        const msg = err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to send invitation';
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [email, role, organizationId, onOpenChange, onSuccess, idToken]);

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setEmail('');
      setRole('member');
      setError(null);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription>
            Send an invitation email to join your organization. They will receive a link to accept within 7 days.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="invite-email" className="block text-sm font-medium text-foreground mb-1">
              Email Address *
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              disabled={loading}
              autoFocus
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSubmit();
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Role
            </label>
            <Select value={role} onValueChange={(v) => { if (v) setRole(v) }}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="owner">Owner</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {role === 'owner' && 'Full access including role changes and org deletion.'}
              {role === 'admin' && 'Can invite members and update tax/bank settings.'}
              {role === 'member' && 'Read-only access to members list and accounting data.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={loading || !email}>
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
